/**
 * Facturación, del lado del panel.
 *
 * Las formas del servidor se generan en `api/types.gen.ts` con los mismos
 * nombres que se usaban a mano, así que aquí sólo quedan los formateadores y el
 * puñado de cosas que no cruzan por la red.
 */

import type { BillingProfile, Payment } from '../../api/types.gen'

export type {
  BankAccount,
  BillingOverview,
  BillingProfile,
  BillingUsage,
  PadronRuc,
  Payment,
  Plan,
  Subscription,
  TransferInstructions,
} from '../../api/types.gen'

/**
 * Con RUC se emite factura; sin RUC, boleta. Es la regla de SUNAT.
 *
 * Se deriva del perfil generado en vez de repetir la lista: si mañana entra un
 * documento nuevo en el backend, aquí llega solo. `NonNullable` porque en el
 * perfil el campo puede no estar puesto todavía, pero el selector siempre
 * ofrece uno de los cuatro.
 */
export type TaxDocType = NonNullable<BillingProfile['tax_doc_type']>

/** Importes en céntimos de sol; el backend nunca manda decimales. */
export function formatSoles(cents: number): string {
  return new Intl.NumberFormat('es-PE', {
    style: 'currency',
    currency: 'PEN',
    minimumFractionDigits: 2,
  }).format(cents / 100)
}

export function formatDate(iso: string | null): string {
  if (!iso) return '—'

  return new Intl.DateTimeFormat('es-PE', { day: 'numeric', month: 'long', year: 'numeric' }).format(
    new Date(iso),
  )
}

/**
 * El número del comprobante como lo escribe ChambaTotal, que es quien lo emite:
 * «F002-00000042». El mismo que sale en el PDF y en el diálogo.
 */
export function invoiceNumber(payment: Payment): string | null {
  if (payment.series === null || payment.number === null) return null

  return `${payment.series}-${String(payment.number).padStart(8, '0')}`
}
